import { FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { faCheck, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { ButtonColor } from "@/components/Button/types";
import { Icon, Modal } from "@/components";
import { useSnackBar } from "@/utils/useSnackbar";
import { classNames } from "@/utils/classNames";

import {
	useDisconnectSalesforceOAuthMutation,
	useGetSalesforceOAuthStatusQuery,
} from "@/pages/Private/redux/salesforce/salesforce.api";

import { SalesforceConnectionTab } from "./SalesforceConnectionTab";
import { SalesforceDealConfigTab } from "./SalesforceDealConfigTab";
import { SalesforceFieldMappingModal } from "./SalesforceFieldMappingModal";
import { SalesforceFieldMappingPanel } from "./SalesforceFieldMappingPanel";
import { SalesforceLeadConfigTab } from "./SalesforceLeadConfigTab";
import { SalesforceSyncLogsTab } from "./SalesforceSyncLogsTab";
import { SalesforceSyncSettingsTab } from "./SalesforceSyncSettingsTab";
import { SalesforceUserMappingTab } from "./SalesforceUserMappingTab";

/**
 * Seven tabs — one more than the Dynamics shell, which reaches its field mapping only through
 * a modal on the connection tab. Salesforce's mapping surface is where a customer spends most
 * of their setup time (four sObjects, restricted picklists, AI fields), so it is a first-class
 * tab; the modal entry point stays for the connection tab's button and renders the same panel.
 */
enum SalesforceTabs {
	CONNECTION = "CONNECTION",
	SETTINGS = "SETTINGS",
	FIELD_MAPPING = "FIELD_MAPPING",
	DEAL_CONFIGURATION = "DEAL_CONFIGURATION",
	LEAD_CONFIGURATION = "LEAD_CONFIGURATION",
	USER_MAPPING = "USER_MAPPING",
	SYNC_LOGS = "SYNC_LOGS",
}

export const SalesforceSettings: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`company.salesforce.${key}`), [t]);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const [searchParams, setSearchParams] = useSearchParams();
	const currentTab =
		(searchParams.get("salesforceTab") as SalesforceTabs) || SalesforceTabs.CONNECTION;

	const [isFieldMappingOpen, setIsFieldMappingOpen] = useState(false);
	const [showDisconnectModal, setShowDisconnectModal] = useState(false);

	const { data: oauthStatus, refetch: refetchOAuthStatus } = useGetSalesforceOAuthStatusQuery();
	const [disconnectOAuth, { isLoading: isDisconnecting }] = useDisconnectSalesforceOAuthMutation();

	const isConnected = oauthStatus?.connected ?? false;

	const handleTabChange = useCallback(
		(tab: SalesforceTabs) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);

					next.set("salesforceTab", tab);

					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const handleDisconnectOAuth = async () => {
		try {
			await disconnectOAuth().unwrap();
			setShowDisconnectModal(false);
		} catch (error) {
			const status = (error as { status?: number })?.status;

			// Already gone on the server — refetch so the tab agrees, and say so rather than
			// reporting a failure the customer cannot act on.
			if (status === 400) {
				setShowDisconnectModal(false);
				handleOpenSnackbar(ts("oauth.alreadyDisconnected"));
				refetchOAuthStatus();

				return;
			}

			handleOpenSnackbar(ts("oauth.disconnectError"));
		}
	};

	const tabItems = [
		{ key: SalesforceTabs.CONNECTION, label: ts("tabs.connection") },
		{ key: SalesforceTabs.SETTINGS, label: ts("tabs.settings") },
		{ key: SalesforceTabs.FIELD_MAPPING, label: ts("tabs.fieldMapping") },
		{ key: SalesforceTabs.DEAL_CONFIGURATION, label: ts("tabs.dealConfiguration") },
		{ key: SalesforceTabs.LEAD_CONFIGURATION, label: ts("tabs.leadConfiguration") },
		{ key: SalesforceTabs.USER_MAPPING, label: ts("tabs.userMapping") },
		{ key: SalesforceTabs.SYNC_LOGS, label: ts("tabs.syncLogs") },
	];

	const connectionContent = (
		<SalesforceConnectionTab
			isDisconnecting={isDisconnecting}
			oauthStatus={oauthStatus}
			ts={ts}
			onOpenFieldMapping={() => setIsFieldMappingOpen(true)}
			onRequestDisconnect={() => setShowDisconnectModal(true)}
		/>
	);

	return (
		<>
			<div className="rounded-xl border border-border-light bg-brand-white p-4">
				<div className="mb-4 flex items-center justify-between">
					<div className="text-smd">{ts("title")}</div>
					{isConnected ? (
						<div
							className="flex items-center gap-1 text-sm text-status-success"
							data-testid="salesforce-connected"
						>
							<Icon className="h-4 w-4" icon={faCheck} />
							{ts("connected")}
						</div>
					) : (
						<div
							className="flex items-center gap-1 text-sm text-text-meta"
							data-testid="salesforce-not-connected"
						>
							<Icon className="h-4 w-4" icon={faXmark} />
							{ts("notConnected")}
						</div>
					)}
				</div>

				{isConnected ? (
					<>
						<div className="mb-4 flex flex-wrap border-b border-border-light">
							{tabItems.map((tab) => (
								<button
									key={tab.key}
									className={classNames(
										"cursor-pointer border-b-2 px-4 py-2 text-sm font-medium transition-colors",
										currentTab === tab.key
											? "border-brand-light text-brand-light"
											: "border-transparent text-text-meta hover:border-border-light hover:text-text"
									)}
									data-testid={`salesforce-tab-${tab.key}`}
									type="button"
									onClick={() => handleTabChange(tab.key)}
								>
									{tab.label}
								</button>
							))}
						</div>

						{currentTab === SalesforceTabs.CONNECTION && connectionContent}
						{currentTab === SalesforceTabs.SETTINGS && (
							<SalesforceSyncSettingsTab isConnected={isConnected} ts={ts} />
						)}
						{currentTab === SalesforceTabs.FIELD_MAPPING && <SalesforceFieldMappingPanel />}
						{currentTab === SalesforceTabs.DEAL_CONFIGURATION && <SalesforceDealConfigTab />}
						{currentTab === SalesforceTabs.LEAD_CONFIGURATION && <SalesforceLeadConfigTab />}
						{currentTab === SalesforceTabs.USER_MAPPING && <SalesforceUserMappingTab />}
						{currentTab === SalesforceTabs.SYNC_LOGS && <SalesforceSyncLogsTab />}
					</>
				) : (
					connectionContent
				)}
			</div>

			{isFieldMappingOpen && (
				<SalesforceFieldMappingModal
					isOpen={isFieldMappingOpen}
					onClose={() => setIsFieldMappingOpen(false)}
				/>
			)}

			{showDisconnectModal && (
				<Modal
					handleClose={() => setShowDisconnectModal(false)}
					handleSave={handleDisconnectOAuth}
					isLoading={isDisconnecting}
					isOpened={showDisconnectModal}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={ts("disconnect")}
					title={ts("oauth.disconnectTitle")}
				>
					<div className="text-sm">{ts("oauth.disconnectBody")}</div>
				</Modal>
			)}

			{renderMessageSnackbar()}
		</>
	);
};
